// src/annihilation/skilltrees/tomeTree.js
// ============================================================
//  ANNIHILATION — the Tome Tree.
//  Source of truth: the POWER DOCTRINE (PRIME). Supersedes
//  ANNIHILATION_SKILL_TREES (v2) Part 5 entirely.
//
//  ⚑⚑ REBUILT (Power Doctrine · Jul 25 2026) ⚑⚑
//  The old fifteen nodes were a GEAR-OPTIMIZER TREE with the
//  word "Tome" pasted over "equipment": equip slots, salvage,
//  set bonuses, drop rates, durability. None of that is a Tome.
//
//  A Tome is a specific, brilliant object: placed face-down at
//  no cost, it waits, it costs Chao to FIRE, then it is Ruined
//  unless it carries a tag. See tomes.js — THE SETTING.
//
//  So the tree is rebuilt around THE VERB. Twelve nodes, every
//  one about placing, waiting, persisting, and getting a Ruined
//  answer back. Acquisition was never the problem.
//
//  WHAT SURVIVED, and why:
//   · Tome Reader (t1) — re-pointed at a Set Tome on the ground.
//   · Extra Slot / Second Extra Slot (t2, t3) — the only two
//     nodes in the old tree doing real work. They now grow the
//     LUXMOIRE, which is the field-ready set.
//   · Quick Swap (t4) → Quick Setting.
//   · Lineage Resonance (t10) — kept, percentage FLATTENED to
//     +2 per THE SMALL NUMBER LAW.
//   · The Tome of Tomes (t15 → t12) — the completionist carrot
//     is good and it survives the reframe intact.
//
//  WHAT DIED: Tome Stacking, Material Efficiency, Ancient
//  Knowledge, Tome Mastery, Cross-Tome, Tome Salvage, Tome
//  Preservation, Rare Knowledge, Master Artisan. Nine nodes
//  about OWNING objects, in a tree that is now about USING them.
//
//  ⚑ NODE IDS ARE REUSED DELIBERATELY (tome_t1…tome_t12). This
//  is an intentional, flagged schema break: a save holding
//  tome_t13/14/15 gets those points RETURNED automatically by
//  treeEngine.reconcileTreeState — points are scarce and
//  permanent, so a deleted node may never silently eat one.
//
//  Pure data; treeEngine is the only legality gate.
// ============================================================

import { NODE_TYPES, TREE_IDS, AENERGY_REGISTER } from "./treeEngine";

// DIAL: the one number that opens the tree. Priced identically
// to the Impera gate, which lands it at L30 for the Tiger /
// Dragon / Z4Z and L28 for Xulan — the being with the deepest
// Chao pool reaches the Tome tree first, and that reads exactly
// right for a caster.
export const TOME_TREE_REQ = AENERGY_REGISTER.CHAMPION_HARD;

const P = NODE_TYPES.PASSIVE;
const A = NODE_TYPES.ACTIVE;
const C = NODE_TYPES.CAPSTONE;

const t = (index, name, type, effect, extra = {}) => ({
  id: `tome_t${index}`,
  name,
  type,
  cluster: "tome",
  aenergyReq: TOME_TREE_REQ, // whole tree opens on Chao, not on a tier
  effect,
  ...extra,
});

export const TOME_TREE = {
  treeId: TREE_IDS.TOME,
  name: "Tome Tree",
  nodeSlots: 12,
  nodes: [
    t(1, "Tome Reader", P,
      "A Set Tome's condition, cadence tag and fire cost are readable at a glance, on the ground, without hover delay."),

    t(2, "The Third Answer", A,
      "Luxmoire capacity 2 to 3. One more answer you get to lay down before the room knows you are there."),

    t(3, "The Fourth Answer", A,
      "Luxmoire capacity 3 to 4. Deliberately expensive — four answers is a strategy, not a loadout.",
      { aenergyReq: AENERGY_REGISTER.LORD_SOFT }),

    t(4, "Quick Setting", P,
      "Setting a Tome takes 0.3 sec instead of 1.5 sec. Safe to place mid-fight."),

    t(5, "Set in Anger", P,
      "Level 1 Tomes may be Set in combat, once per engagement. The foresight tax, paid back exactly once."),

    t(6, "The Long Wait", P,
      "A Set Tome persists across engagements until it fires. Normally they dissolve when combat closes."),

    t(7, "Second Reading", P,
      "Once per rest, re-Set one Ruined Tome without returning to the village."),

    t(8, "Fare Well", P,
      "Persistent Tomes (OPT/OP2/OP3/ATV) fire on a beat one shorter than their tag. You paid to light the fire; the fire burns faster."),

    t(9, "Refusal", P,
      "Your Set Tomes cannot be destroyed by enemy Omnia effects. The ward stays warded."),

    t(10, "Lineage Resonance", P,
      "Tomes matching your lineage grant +2 flat to their effect.",
      // ⚑ SMALL NUMBER LAW: was "+30%". Against a stat scale that
      // naturally lives in 0–10, a percentage is either
      // meaningless or catastrophic. Flat +2 is a real grant.
      { flatBonus: 2 }),

    t(11, "The Ninth Answer", C,
      "One Level 3 Tome may be held that fires TWICE before Ruin. The true instant, said twice.",
      { aenergyReq: AENERGY_REGISTER.LORD_HARD }),

    t(12, "The Tome of Tomes", C,
      "Unlocks the 'Tome of Tomes' meta-slot — when a Luxmoire slot is left empty, every Tome you have ever owned contributes a tiny passive bonus to it. The completionist's endgame carrot.",
      { aenergyReq: AENERGY_REGISTER.LORD_HARD }),
  ],
};

export default TOME_TREE;
